import React, { useState } from "react";
import Portlet from "components/Portlet";
import PortletHeader from "components/PortletHeader";
import PortletContent from "components/PortletContent";
import PortletLabel from "components/PortletLabel";
import PortletFooter from "components/PortletFooter";
import Loading from "../../../../utils/Loading";
import {
    Table,
    TableRow,
    TableCell,
    TableHead,
    TableBody,
    Button,
    Popover,
    Typography,
    TableContainer,
    Paper,
    Grid,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Period from "../../../../helpers/PeriodWeighted";
import Filter from "../../../../helpers/Filter";
import { getSpecialReportTypes } from "../../../../utils/fns/get-special-report-type";
import moment from "moment";
import "moment/locale/es";
import { useObjectivesAvailabilityByDay } from "../../../../hooks/useObjectivesAvailabilityByDay";
import { generateCsv } from "../../../../utils/fns/generate-csv";
import { generateExcelFromJson } from "../../../../utils/fns/generate-excel";
moment.locale("es");

/**
 * Estilos del componente
 */
const styles = {
    tableHeader: { backgroundColor: "orange", color: "white", padding: 2 },
    uptimeCell: { backgroundColor: "#51d663", color: "white" },
    downtimeCell: { backgroundColor: "#f24b3f", color: "white" },
    defaultCell: { backgroundColor: "#cfcfcf", color: "white" },
    blueCell: { backgroundColor: "#1976d2", color: "white" },
};

/**
 * @typedef {Object} Props
 * @property {object[]} data - Data general del objetivo
 *
 * @property {Props} props - Download action title
 * @returns React.Component
 *
 * @author David Maldonado
 * @version 1.0.0
 */
const UptimeTable = ({ data }) => {
    const {
        match: {
            params: { nombre_objetivo: nameObjective },
        },
    } = data;
    const [periodEl, setPeriodEl] = useState(null);
    const [period, setPeriod] = useState("d");
    const [namePeriod, setNamePeriod] = useState("Rango: último día");
    const [statePeriod, setStatePeriod] = useState({});
    const [scheduleId, setScheduleId] = useState(0);
    const [scheduleName, setScheduleName] = useState("Todo horario");
    const horariosFromStorage = sessionStorage.getItem("horarios");
    const horariosList = JSON.parse(horariosFromStorage || "[]");
    const { isCsv, isHtml, isXlsx } = getSpecialReportTypes();
    const {
        isLoading,
        getDataFromApi,
        data: objectivesData,
    } = useObjectivesAvailabilityByDay({
        period,
        statePeriod,
        scheduleId,
        autoFetch: isHtml,
    });

    const selectPeriod = (
        prevPeriod,
        prevTypeGraph,
        statePeriod,
        nextPeriod
    ) => {
        let periodConfig = {
            period: "d",
            namePeriod: "Rango: último día",
            statePeriod: {},
        };
        switch (nextPeriod) {
            case "3dia":
                periodConfig = {
                    period: "3d",
                    namePeriod: "Rango: últimos 3 días",
                    statePeriod: {},
                };
                break;
            case "semana":
                periodConfig = {
                    period: "w",
                    namePeriod: "Rango: última semana",
                    statePeriod: {},
                };
                break;
            case "custom":
                periodConfig = {
                    period: "c",
                    namePeriod:
                        "Rango: " +
                        moment(statePeriod.startDate).format("YYYY-MM-DD") +
                        " a " +
                        moment(statePeriod.endDate).format("YYYY-MM-DD"),
                    statePeriod: statePeriod,
                };
                break;
            default:
                break;
        }

        setPeriod(periodConfig.period);
        setNamePeriod(periodConfig.namePeriod);
        setStatePeriod(periodConfig.statePeriod);
        setPeriodEl(null);
    };

    const handleClickDownloadCsv = async () => {
        if (isHtml) callCsv(objectivesData);
        if (!isHtml) {
            const remoteData = await getDataFromApi();
            callCsv(remoteData);
        }
    };

    const handleClickXlsx = async () => {
        const headers = [
            "Objetivo",
            "Fecha",
            "Uptime",
            "Downtime",
            "No Monitoreo",
        ];
        const data = isHtml ? objectivesData : await getDataFromApi();
        const dataMapped = data.map(
            ({ name, fecha, data: { uptime, downtime, noMonitoreo } }) => {
                return {
                    name,
                    fecha,
                    uptime,
                    downtime,
                    noMonitoreo,
                };
            }
        );
        generateExcelFromJson(dataMapped, headers, [scheduleName]);
    };

    const callCsv = (data) => {
        let csv = `Objetivos;\n${scheduleName};\nFecha;Objetivo;Uptime;Downtime;No Monitoreo;\n`;
        data.forEach(function (item) {
            csv += `${item.fecha};${item.name};${item.data.uptime};${item.data.downtime};${item.data.noMonitoreo};`;
            csv += "\n";
        });
        generateCsv({ name: "disponibilidad", data: csv });
    };

    return (
        <Portlet>
            <PortletHeader>
                <PortletLabel title={nameObjective} />
                <Button
                    style={{
                        fontSize: "15px",
                        float: "left",
                        color: "#66788A",
                    }}
                    onClick={(e) => setPeriodEl(e.currentTarget)}
                    disabled={isLoading}
                >
                    {namePeriod}
                    <ArrowDropDownIcon />
                </Button>
            </PortletHeader>
            <PortletContent>
                <Grid container>
                    {(isCsv || isXlsx) && (
                        <>
                            {horariosList.length && (
                                <Grid
                                    item
                                    lg={4}
                                    md={6}
                                    sm={12}
                                    style={{ marginBottom: 15 }}
                                >
                                    <Filter
                                        list={horariosList}
                                        label="Horarios"
                                        placeholder="Filtro de Horarios"
                                        InfoAutoComplete={(label, value) => {
                                            setScheduleId(value.horario_id);
                                            setScheduleName(value.nombre);
                                        }}
                                        disabled={isLoading}
                                    />
                                </Grid>
                            )}
                            <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                style={{ marginBottom: 10 }}
                            >
                                {isCsv && (
                                    <Button
                                        onClick={handleClickDownloadCsv}
                                        style={{ marginBottom: 2 }}
                                        disabled={isLoading}
                                    >
                                        <Typography
                                            variant="button"
                                            style={{ color: "#66788A" }}
                                            disabled={isLoading}
                                        >
                                            {isCsv.name}
                                        </Typography>
                                    </Button>
                                )}
                                {isXlsx && (
                                    <Button
                                        onClick={handleClickXlsx}
                                        style={{ marginBottom: 2 }}
                                        disabled={isLoading}
                                    >
                                        <Typography
                                            variant="button"
                                            style={{ color: "#66788A" }}
                                            disabled={isLoading}
                                        >
                                            {isXlsx.name}
                                        </Typography>
                                    </Button>
                                )}
                            </Grid>
                            {!isHtml && isLoading ? (
                                <Loading text="Generando archivo" />
                            ) : null}
                        </>
                    )}
                </Grid>
                {isLoading && isHtml ? (
                    <Loading />
                ) : (
                    <>
                        {isHtml && (
                            <TableContainer component={Paper}>
                                <Table size="small" aria-label="a dense table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Objetivo</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell
                                                align="center"
                                                style={styles.tableHeader}
                                            >
                                                Uptime
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={styles.tableHeader}
                                            >
                                                Downtime
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={styles.tableHeader}
                                            >
                                                No monitoreo
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {objectivesData.map(
                                            ({ name, fecha, data }) => (
                                                <TableRow
                                                    key={`${name}${fecha}`}
                                                >
                                                    <TableCell>
                                                        {name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {fecha}
                                                    </TableCell>
                                                    <TableCell
                                                        style={
                                                            styles.uptimeCell
                                                        }
                                                        align="right"
                                                    >
                                                        {data.uptime} %
                                                    </TableCell>
                                                    <TableCell
                                                        style={
                                                            styles.downtimeCell
                                                        }
                                                        align="right"
                                                    >
                                                        {data.downtime} %
                                                    </TableCell>
                                                    <TableCell
                                                        style={
                                                            styles.defaultCell
                                                        }
                                                        align="right"
                                                    >
                                                        {data.noMonitoreo} %
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </PortletContent>
            <PortletFooter>
                <Popover
                    anchorEl={periodEl}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    onClose={() => setPeriodEl(null)}
                    open={Boolean(periodEl)}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                >
                    <Period onClick={selectPeriod} period={period} />
                </Popover>
            </PortletFooter>
        </Portlet>
    );
};

export { UptimeTable };
